<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0 d-flex align-items-center flex-wrap gap-2">
            <div>
              <h6 class="mb-1">
                Editar Pedido
                <span v-if="order" class="text-primary ms-1">#{{ order.id }}</span>
              </h6>
              <span v-if="order" class="text-xs text-secondary">
                {{ order.customer_name || '—' }} · {{ formatDate(order.created_at) }}
              </span>
            </div>
            <button type="button" class="btn btn-outline-secondary btn-sm mb-0 ms-auto" @click="goBack">
              <i class="fas fa-arrow-left me-1"></i>Órdenes
            </button>
          </div>
          <div class="card-body">
            <div v-if="isLoading" class="text-center py-4">
              <i class="fas fa-spinner fa-spin me-1"></i>
              Cargando pedido...
            </div>
            <p v-else-if="!order" class="text-muted mb-0">Pedido no encontrado.</p>
            <p v-else-if="!canEditItems" class="text-muted mb-0">
              Este pedido ya fue sincronizado con Shopify y no se puede editar.
            </p>
            <template v-else>
              <h6 class="text-uppercase text-secondary text-xxs font-weight-bolder mb-2">Productos</h6>
              <div class="order-items-editor">
                <div class="position-relative mb-3">
                  <input
                    v-model="productSearch"
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Buscar producto para agregar..."
                    @input="onProductSearch"
                  />
                  <div v-if="productSearch.trim()" class="order-items-search-results card shadow-sm">
                    <div v-if="isSearchingProducts" class="text-xs text-muted p-2">
                      <i class="fas fa-spinner fa-spin me-1"></i>Buscando...
                    </div>
                    <div v-else-if="!productResults.length" class="text-xs text-muted p-2">Sin resultados</div>
                    <template v-else>
                      <div v-for="product in productResults" :key="product.id" class="border-bottom">
                        <button
                          type="button"
                          class="order-items-search-row"
                          @click="onProductResultClick(product)"
                        >
                          <span class="text-sm">{{ product.title }}</span>
                          <span class="text-xs text-secondary">
                            Stock: {{ product.inventory_quantity ?? 0 }} · {{ formatMoney(product.price_min ?? product.price_max ?? 0, order.currency) }}
                            <i v-if="productVariants(product).length > 1" class="fas ms-1" :class="expandedProductId === product.id ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                          </span>
                        </button>
                        <div v-if="expandedProductId === product.id" class="ps-3 pb-1">
                          <button
                            v-for="variant in productVariants(product)"
                            :key="variant.id"
                            type="button"
                            class="order-items-search-row"
                            :disabled="!allowBackorder && Number(variant.inventory_quantity || 0) <= 0"
                            @click="addEditItem(product, variant)"
                          >
                            <span class="text-xs">{{ variant.title }}</span>
                            <span class="text-xs text-secondary">
                              Stock: {{ variant.inventory_quantity ?? 0 }} · {{ formatMoney(variant.price, order.currency) }}
                            </span>
                          </button>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>

                <div class="table-responsive">
                  <table class="table align-items-center mb-0">
                    <thead>
                      <tr>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Producto</th>
                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cantidad</th>
                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Precio</th>
                        <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, idx) in editItems" :key="`${item.shopify_inventory_id}-${idx}`">
                        <td>
                          <span class="text-sm font-weight-bold d-block">{{ item.title }}</span>
                          <span v-if="item.sku" class="text-xs text-secondary d-block">SKU: {{ item.sku }}</span>
                        </td>
                        <td class="text-center" style="width: 110px">
                          <input
                            v-model.number="item.quantity"
                            type="number"
                            min="1"
                            class="form-control form-control-sm text-center"
                          />
                        </td>
                        <td class="text-center text-sm">{{ formatMoney(item.unit_price, order.currency) }}</td>
                        <td class="text-center text-sm">{{ formatMoney(editLineTotal(item), order.currency) }}</td>
                        <td class="text-center">
                          <button
                            type="button"
                            class="btn btn-link btn-sm text-danger mb-0 p-1"
                            title="Quitar producto"
                            @click="removeEditItem(idx)"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                      <tr v-if="!editItems.length">
                        <td colspan="5" class="text-center text-muted text-xs">Agrega al menos un producto</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="d-flex flex-wrap align-items-center gap-2 mt-3">
                  <label class="d-flex align-items-center text-xs mb-0" style="gap: 6px">
                    <input v-model="allowBackorder" type="checkbox" />
                    Permitir sobrepedido
                  </label>
                  <div class="ms-auto d-flex gap-2">
                    <button type="button" class="btn btn-outline-secondary btn-sm mb-0" :disabled="isSavingItems" @click="goBack">
                      Volver
                    </button>
                    <button
                      type="button"
                      class="btn btn-primary btn-sm mb-0"
                      :disabled="isSavingItems || !editItems.length"
                      @click="saveEditItems"
                    >
                      <i class="fas me-1" :class="isSavingItems ? 'fa-spinner fa-spin' : 'fa-save'"></i>Guardar productos
                    </button>
                  </div>
                </div>
              </div>

              <div class="row mt-4">
                <div class="col-md-6 offset-md-6">
                  <table class="table table-sm">
                    <tbody>
                      <tr>
                        <td class="text-secondary text-xs">Subtotal</td>
                        <td class="text-sm text-end">{{ formatMoney(editTotals.subtotal, order.currency) }}</td>
                      </tr>
                      <tr>
                        <td class="text-secondary text-xs">Descuentos</td>
                        <td class="text-sm text-end">{{ formatMoney(editTotals.discount, order.currency) }}</td>
                      </tr>
                      <tr v-if="Number(order.freight_amount) > 0">
                        <td class="text-secondary text-xs">Flete</td>
                        <td class="text-sm text-end">{{ formatMoney(order.freight_amount, order.currency) }}</td>
                      </tr>
                      <tr>
                        <td class="text-sm font-weight-bold">Total</td>
                        <td class="text-sm font-weight-bold text-end">{{ formatMoney(editTotals.total, order.currency) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import shopifyService from "@/services/shopify.service";
import Swal from "sweetalert2";

// Edición de productos de una venta POS que aún no se sincroniza con Shopify.
export default {
  name: "ShopifyOrderEdit",
  data() {
    return {
      order: null,
      isLoading: false,
      isSavingItems: false,
      editItems: [],
      productSearch: "",
      productResults: [],
      isSearchingProducts: false,
      productSearchTimeout: null,
      expandedProductId: null,
      allowBackorder: false,
    };
  },
  computed: {
    canEditItems() {
      return this.order?.source === 'pos' && !this.order?.shopify_order_id;
    },
    editTotals() {
      const percent = Math.min(Math.max(Number(this.order?.discount_percent) || 0, 0), 100);
      let subtotal = 0;
      let discount = 0;
      this.editItems.forEach((item) => {
        const line = (Number(item.unit_price) || 0) * (Number(item.quantity) || 0);
        subtotal += line;
        if (item.apply_discount) discount += line * (percent / 100);
      });
      const freight = Number(this.order?.freight_amount) || 0;
      return { subtotal, discount, total: Math.max(subtotal - discount + freight, 0) };
    },
  },
  mounted() {
    this.fetchOrder();
  },
  beforeUnmount() {
    clearTimeout(this.productSearchTimeout);
  },
  methods: {
    async fetchOrder() {
      const id = this.$route.query.id;
      if (!id) return;
      this.isLoading = true;
      try {
        const response = await shopifyService.getOrder('pos', id);
        this.order = response?.data?.data ?? null;
        if (this.canEditItems) {
          this.startEditingItems();
        }
      } catch (error) {
        console.error("Error al cargar el pedido:", error);
        this.order = null;
      } finally {
        this.isLoading = false;
      }
    },
    startEditingItems() {
      this.editItems = (Array.isArray(this.order?.line_items) ? this.order.line_items : []).map((item) => ({
        shopify_inventory_id: item.shopify_inventory_id,
        shopify_product_id: item.shopify_product_id ?? null,
        shopify_variant_id: item.shopify_variant_id ?? null,
        sku: item.sku ?? null,
        title: item.title || item.name || 'Producto POS',
        handle: item.handle ?? null,
        quantity: Number(item.quantity) || 1,
        unit_price: Number(item.unit_price ?? item.price) || 0,
        apply_discount: !!item.apply_discount,
      }));
      this.productSearch = "";
      this.productResults = [];
      this.expandedProductId = null;
    },
    goBack() {
      this.$router.push({ name: 'Shopify Ordenes' });
    },
    onProductSearch() {
      clearTimeout(this.productSearchTimeout);
      this.expandedProductId = null;
      if (!this.productSearch.trim()) {
        this.productResults = [];
        return;
      }
      this.productSearchTimeout = setTimeout(() => this.searchProducts(), 350);
    },
    async searchProducts() {
      this.isSearchingProducts = true;
      try {
        const response = await shopifyService.getPosProducts({
          page: 1,
          search: this.productSearch.trim(),
          per_page: 10,
          incluir_sin_stock: this.allowBackorder ? 1 : undefined,
        });
        this.productResults = response?.data?.data || [];
      } catch (error) {
        console.error("Error al buscar productos:", error);
        this.productResults = [];
      } finally {
        this.isSearchingProducts = false;
      }
    },
    productVariants(product) {
      return Array.isArray(product?.variants) ? product.variants : [];
    },
    onProductResultClick(product) {
      const variants = this.productVariants(product);
      if (variants.length > 1) {
        this.expandedProductId = this.expandedProductId === product.id ? null : product.id;
        return;
      }
      if (!this.allowBackorder && Number(product.inventory_quantity || 0) <= 0) return;
      this.addEditItem(product, variants[0] || null);
    },
    // Mismo armado de ítem que el POS: el inventario se identifica por el
    // inventory_item_id de la variante o, si no tiene, por el id del producto.
    addEditItem(product, variant) {
      const inventoryId = variant?.inventory_item_id || product.id;
      const existing = this.editItems.find((item) =>
        Number(item.shopify_inventory_id) === Number(inventoryId)
      );
      if (existing) {
        existing.quantity = (Number(existing.quantity) || 0) + 1;
      } else {
        const hasVariantTitle = variant && variant.title && variant.title !== 'Default Title'
          && this.productVariants(product).length > 1;
        this.editItems.push({
          shopify_inventory_id: inventoryId,
          shopify_product_id: product.shopify_product_id ?? null,
          shopify_variant_id: variant?.id ?? null,
          sku: variant?.sku || null,
          title: hasVariantTitle ? `${product.title} - ${variant.title}` : product.title,
          handle: product.handle ?? null,
          quantity: 1,
          unit_price: Number(variant?.price) || Number(product.price_min ?? product.price_max) || 0,
          apply_discount: false,
        });
      }
      this.productSearch = "";
      this.productResults = [];
      this.expandedProductId = null;
    },
    removeEditItem(idx) {
      this.editItems.splice(idx, 1);
    },
    editLineTotal(item) {
      const percent = Math.min(Math.max(Number(this.order?.discount_percent) || 0, 0), 100);
      const line = (Number(item.unit_price) || 0) * (Number(item.quantity) || 0);
      return item.apply_discount ? line * (1 - percent / 100) : line;
    },
    async saveEditItems() {
      if (this.editItems.some((item) => !Number.isInteger(item.quantity) || item.quantity < 1)) {
        await Swal.fire("Cantidad inválida", "Cada producto debe tener una cantidad entera mayor a 0.", "warning");
        return;
      }
      this.isSavingItems = true;
      try {
        const response = await shopifyService.updatePosSaleItems(this.order.id, {
          permitir_sobrepedido: this.allowBackorder,
          items: this.editItems,
        });
        const data = response?.data || {};
        if (!data.success) {
          throw new Error(data.message || "No se pudieron guardar los productos.");
        }
        this.order = { ...this.order, ...data.order };
        // El picking se reconcilia con los nuevos productos.
        try {
          await shopifyService.initPicking(this.order.id);
        } catch (error) {
          console.error("Error al actualizar el picking:", error);
        }
        await Swal.fire(
          "Productos actualizados",
          data.payments_covered
            ? "El pedido se actualizó correctamente."
            : "El pedido se actualizó, pero los pagos registrados ya no cubren el nuevo total.",
          data.payments_covered ? "success" : "warning"
        );
        this.goBack();
      } catch (error) {
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "No se pudieron guardar los productos.",
          "error"
        );
      } finally {
        this.isSavingItems = false;
      }
    },
    formatMoney(value, currency = "COP") {
      if (value == null) return "—";
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: currency || "COP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    },
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString("es-CO", {
        dateStyle: "short",
        timeStyle: "short",
      });
    },
  },
};
</script>

<style scoped>
.order-items-search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 320px;
  overflow-y: auto;
}
.order-items-search-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border: 0;
  background: transparent;
  text-align: left;
}
.order-items-search-row:hover:not(:disabled) {
  background: #f1f3f5;
}
.order-items-search-row:disabled {
  opacity: 0.5;
}
</style>
